'use client';

import * as React from 'react';
import { motion, useReducedMotion } from 'motion/react';
import Image from 'next/image';

import { IntegrationLogos } from '~/components/integration-logos';
import { INTEGRATIONS_CONTENT } from '~/lib/landing-content';

/**
 * Brand-logo resolver. Each integration is rendered either:
 *   - 'png'  → /assets/integrations/<id>.png via next/image (logo.dev fetch)
 *   - 'icon' → React component (react-icons brand mark or our wordmark
 *     fallback for brands logo.dev didn't have)
 */
type LogoSource =
  | { kind: 'png'; id: string }
  | { kind: 'icon'; Component: React.ComponentType<{ className?: string }> };

const LOGO_SOURCES: Record<string, LogoSource> = {
  hubspot:  { kind: 'png',  id: 'hubspot' },
  attio:    { kind: 'png',  id: 'attio' },
  outlook:  { kind: 'png',  id: 'outlook' },
  linkedin: { kind: 'png',  id: 'linkedin' },
  // Calendly switched from PNG to react-icons SVG — the logo.dev PNG has
  // internal whitespace around the mark, so it visually rendered smaller
  // than the other brand cells. SiCalendly is the official C wordmark.
  calendly: { kind: 'icon', Component: IntegrationLogos.Calendly },
  gmail:     { kind: 'icon', Component: IntegrationLogos.Gmail },
  gcal:      { kind: 'icon', Component: IntegrationLogos.GoogleCalendar },
  salesforce:{ kind: 'icon', Component: IntegrationLogos.Salesforce },
  // MCP renders as a combo of the official mark (from modelcontextprotocol.io
  // via logo.dev) + "MCP" wordmark text, since the abstract mark alone is
  // unlabeled. Component lives in integration-logos.tsx.
  mcp:       { kind: 'icon', Component: IntegrationLogos.MCP },
};

/**
 * Integrations grid — 4 cols × 4 rows lattice with the callout in the center
 * 2×2 block. Reverted from the 6×6 mosaic (too many hatched dead cells) to a
 * tighter focused layout.
 *
 *   ┌──────────┬──────────┬──────────┬──────────┐
 *   │ HubSpot  │  MCP     │ Outlook  │ LinkedIn │  row 1
 *   ├──────────┼─────────────────────┼──────────┤
 *   │  Attio   │                     │ Calendly │  row 2
 *   ├──────────┤   CENTER CALLOUT    ├──────────┤
 *   │ Domain   │  (eyebrow + h2 +    │  Gmail   │  row 3
 *   │ provis…  │   subtitle)         │          │
 *   ├──────────┼─────────────────────┼──────────┤
 *   │ Monitor  │  G Cal   │ Enrich   │ hat      │  row 4
 *   └──────────┴──────────┴──────────┴──────────┘
 *
 * Cell kinds:
 *   - 'logo'    → third-party brand mark via LOGO_SOURCES
 *   - 'inhouse' → "Built-in" badge + label (Lumnis-built capability)
 *   - 'hatch'   → diagonal-stripe empty cell (visual rhythm)
 *
 * Mobile (< md): callout above a 2-col grid (3-col from sm), shorter cells.
 */
export function IntegrationsSection(): React.JSX.Element {
  const shouldReduceMotion = useReducedMotion();

  const byId = Object.fromEntries(
    INTEGRATIONS_CONTENT.cells.map((c) => [c.id, c])
  ) as Record<string, (typeof INTEGRATIONS_CONTENT.cells)[number]>;

  // 12 cells in row-major order. CSS Grid auto-placement skips the 4 slots
  // covered by the center callout (rows 2-3, cols 2-3), so these flow into:
  //   row 1: cols 1-4 (4 cells)
  //   row 2: col 1 + col 4 (2 cells)
  //   row 3: col 1 + col 4 (2 cells)
  //   row 4: cols 1-4 (4 cells)
  const desktopOrder: (string | 'hatch')[] = [
    // Row 1
    'hubspot', 'mcp', 'outlook', 'linkedin',
    // Row 2 (col 1 + col 4 — center occupies 2,3)
    'attio', 'calendly',
    // Row 3 (col 1 + col 4)
    'domain', 'gmail',
    // Row 4 — Salesforce takes what was the empty hatch slot
    'monitor', 'gcal', 'enrich', 'salesforce',
  ];

  return (
    <section className="relative py-20 md:py-28 bg-[color:var(--paper)]">
      <div className="w-full mx-auto max-w-[1320px] px-6 md:px-8 lg:px-12 xl:px-16">
        {/* ── Mobile (< md): stacked callout + 2-col logo grid ─────────── */}
        <div className="md:hidden">
          <CalloutBlock />
          <div className="mt-8 grid grid-cols-2 sm:grid-cols-3 border-l border-t border-[color:var(--hairline)] rounded-lg overflow-hidden">
            {INTEGRATIONS_CONTENT.cells.map((c) => (
              <Cell key={c.id} cell={c} source={c.kind === 'logo' ? LOGO_SOURCES[c.id] : undefined} />
            ))}
          </div>
        </div>

        {/* ── Desktop (md+): 4×4 lattice with center 2×2 callout ───────── */}
        <motion.div
          className="hidden md:grid relative grid-cols-4 border-l border-t border-[color:var(--hairline)] rounded-lg overflow-hidden"
          style={{
            gridTemplateRows: 'repeat(4, minmax(120px, 1fr))',
          }}
          initial={{ opacity: 0, y: shouldReduceMotion ? 0 : 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-80px' }}
          transition={{ duration: 0.45 }}
        >
          {/* Center 2×2 callout — rows 2-3, cols 2-3 */}
          <div
            className="bg-[color:var(--paper)] border-r border-b border-[color:var(--hairline)] flex items-center justify-center p-5 lg:p-7"
            style={{ gridRow: '2 / span 2', gridColumn: '2 / span 2' }}
          >
            <CalloutBlock />
          </div>

          {/* Surrounding cells in row-major order. Auto-placement skips the
              center span automatically. */}
          {desktopOrder.map((slot, i) => {
            if (slot === 'hatch') return <HatchedCell key={`hatch-${i}`} />;
            const cell = byId[slot];
            if (!cell) return <HatchedCell key={`miss-${slot}-${i}`} />;
            return (
              <Cell
                key={cell.id}
                cell={cell}
                source={cell.kind === 'logo' ? LOGO_SOURCES[cell.id] : undefined}
              />
            );
          })}
        </motion.div>
      </div>
    </section>
  );
}

/* ───────────────────────────────────────────────────────────────
   Center callout, eyebrow, two-tone headline, subtitle.
   Marker-highlights the second half of the title to match the
   hero + How-it-works typographic system.
   ─────────────────────────────────────────────────────────────── */

function CalloutBlock(): React.JSX.Element {
  return (
    <div className="mx-auto flex flex-col items-center text-center max-w-[440px]">
      <span
        className="inline-flex items-center self-center gap-1.5 px-2.5 py-1 rounded-md font-mono text-[10.5px] tracking-[0.1em] uppercase font-medium mb-4"
        style={{ background: 'var(--mint)', color: 'var(--accent-darker)' }}
      >
        {INTEGRATIONS_CONTENT.eyebrow}
      </span>
      <h2 className="display-h2 text-2xl md:text-3xl lg:text-[2rem] text-[color:var(--ink)] [text-wrap:balance] mb-3">
        {INTEGRATIONS_CONTENT.sectionTitleLead}{' '}
        <mark
          style={{
            color: 'var(--accent-darker)',
            padding: '0.02em 0.18em',
            backgroundImage:
              'linear-gradient(100deg, var(--mint) 0%, var(--mint-2) 45%, var(--mint) 100%)',
            borderRadius: '0.45em 0.12em 0.35em 0.18em / 0.18em 0.35em 0.12em 0.45em',
            boxShadow:
              'inset 0 -0.06em 0 color-mix(in srgb, var(--accent-darker) 7%, transparent)',
            WebkitBoxDecorationBreak: 'clone',
            boxDecorationBreak: 'clone',
          }}
        >
          {INTEGRATIONS_CONTENT.sectionTitleRest}
        </mark>
      </h2>
      <p className="text-[13.5px] md:text-[14.5px] text-[color:var(--ink-3)] leading-relaxed">
        {INTEGRATIONS_CONTENT.subtitle}
      </p>
    </div>
  );
}

/* ───────────────────────────────────────────────────────────────
   Cell renderers.
   ─────────────────────────────────────────────────────────────── */

function Cell({
  cell,
  source
}: {
  cell: (typeof INTEGRATIONS_CONTENT.cells)[number];
  source?: LogoSource;
}): React.JSX.Element {
  // Logo cell, third-party brand mark, grayscale at rest, color on hover.
  if (cell.kind === 'logo' && source) {
    return (
      <div className="bg-[color:var(--paper)] border-r border-b border-[color:var(--hairline)] flex items-center justify-center p-4 md:p-6 min-h-[84px] md:min-h-[110px]">
        <div
          className="relative grayscale opacity-70 hover:grayscale-0 hover:opacity-100 transition-all duration-300 flex items-center justify-center"
          style={{ width: 'clamp(80px, 9vw, 120px)', height: 'clamp(32px, 4vw, 48px)' }}
          title={cell.label}
        >
          {source.kind === 'png' && (
            <Image
              src={`/assets/integrations/${source.id}.png`}
              alt={cell.label}
              fill
              sizes="120px"
              className="object-contain"
            />
          )}
          {source.kind === 'icon' && (
            // h-full w-auto forces SVG icons to fill container height; react-
            // icons default to width=1em height=1em which made Gmail/GCal/
            // Calendly render at ~16px while PNGs scaled up to fit. The
            // wordmark spans (MCP) ignore w-auto and size by their text content,
            // which is what we want.
            <source.Component className="h-full w-auto" />
          )}
        </div>
      </div>
    );
  }

  // In-house cell — Lumnis-built capability, mint accent, no third-party logo
  // so visitors do not mistake it for a partner brand.
  return (
    <div className="bg-[color:var(--paper)] border-r border-b border-[color:var(--hairline)] flex flex-col items-center justify-center text-center p-4 md:p-5 min-h-[84px] md:min-h-[110px]">
      <span
        className="font-mono text-[9.5px] tracking-[0.12em] uppercase font-semibold mb-1.5"
        style={{ color: 'var(--accent-deep)' }}
      >
        Built-in
      </span>
      <span className="text-[12.5px] md:text-[13px] font-semibold text-[color:var(--ink)] leading-tight [text-wrap:balance]">
        {cell.label}
      </span>
    </div>
  );
}

function HatchedCell(): React.JSX.Element {
  return (
    <div
      className="hatched-bg border-r border-b border-[color:var(--hairline)] min-h-[110px]"
      aria-hidden
    />
  );
}
